import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import {
  useGetAccountLifecycleCompetitorsQuery,
  useGetAccountLifecycleReasonsQuery,
  useGetAccountLifecycleSettingsQuery,
} from 'api/account-lifecycle/accountLifecycle'
import { useGetBusinessUnitsQuery } from 'api/business-units/businessUnits'
import { selectIsAccountLifecycleCaptureEnabled } from 'data/tenant/tenantSettingsSelectors'

import type { AccountLifecycleCompetitorApi, AccountLifecycleReasonApi } from 'types/api/account-lifecycle/accountLifecycle'

export type NewAccountLifecycle = {
  isEnabled: boolean
  isReady: boolean
  hasLoadError: boolean
  reasons: AccountLifecycleReasonApi[]
  competitors: AccountLifecycleCompetitorApi[]
  requirePhone: boolean
  requireBillingEmail: boolean
  requireBusinessUnit: boolean
  requireSource: boolean
}

const NO_REASONS: AccountLifecycleReasonApi[] = []
const NO_COMPETITORS: AccountLifecycleCompetitorApi[] = []

/**
 * Lifecycle options and required-field rules for the New account drawer, fetched only while it is open.
 * With capture on the rules are unknown until settings load: `isReady` holds the create, `hasLoadError` says so.
 */
const useNewAccountLifecycle = (isOpen: boolean): NewAccountLifecycle => {
  const isEnabled = useSelector(selectIsAccountLifecycleCaptureEnabled)
  const skip = !isEnabled || !isOpen
  const { data: reasonData, isError: hasReasonsError } = useGetAccountLifecycleReasonsQuery({ type: 'WON' }, { skip })
  const { data: competitorData, isError: hasCompetitorsError } = useGetAccountLifecycleCompetitorsQuery(undefined, { skip })
  const { data: settings, isError: hasSettingsError } = useGetAccountLifecycleSettingsQuery(undefined, { skip })
  const { data: businessUnits, isError: hasBusinessUnitsError } = useGetBusinessUnitsQuery(undefined, { skip })

  return useMemo(() => {
    // Only a list a rule depends on holds the create; the business unit dropdown reports its own failure.
    const needsBusinessUnits = Boolean(settings?.requireBusinessUnitOnCreate)
    const needsReasons = Boolean(settings?.requireSourceOnCreate)
    return {
      isEnabled,
      isReady:
        !isEnabled ||
        (Boolean(settings) && (!needsBusinessUnits || businessUnits !== undefined) && (!needsReasons || reasonData !== undefined)),
      hasLoadError:
        isEnabled && (hasSettingsError || hasReasonsError || hasCompetitorsError || (needsBusinessUnits && hasBusinessUnitsError)),
      reasons: reasonData?.reasons ?? NO_REASONS,
      competitors: competitorData?.competitors ?? NO_COMPETITORS,
      requirePhone: isEnabled && Boolean(settings?.requirePhoneOnCreate),
      requireBillingEmail: isEnabled && Boolean(settings?.requireBillingEmailOnCreate),
      requireBusinessUnit: isEnabled && needsBusinessUnits && (businessUnits?.length ?? 0) > 0,
      requireSource: isEnabled && needsReasons,
    }
  }, [
    isEnabled,
    reasonData,
    competitorData,
    settings,
    businessUnits,
    hasSettingsError,
    hasReasonsError,
    hasCompetitorsError,
    hasBusinessUnitsError,
  ])
}

export default useNewAccountLifecycle
